/**
 * Member Detail → Bet List → SportBook / Premiumbook.
 * Lab `getMemberBets` → `POST /v1/bets/getMemberBets`.
 */
import { useQuery } from '@tanstack/react-query'
import { betFetch, hasLiveApi, hasLiveBet } from '@/api/client'
import { useSession } from '@/api/hooks/shared/useSession'
import { useBookmakerSessionFlag } from '@/api/hooks/shared/useStaffMe'
import { keys } from '@/api/hooks/shared/keys'
import {
  dayEndIso,
  dayStartIso,
  memberBetNameFilter,
  omitAllFilter,
  toMemberSportBookBet,
  type GetMemberBetsRequest,
  type GetMemberBetsResponse,
  type MemberBetSource,
  type MemberBetTime,
  type MemberBetType,
  type MemberSportBookBet,
} from '@/api/map/memberBets'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import * as api from '@/data/api'
import { ROLE_LABEL, type Role } from '@/data/types'
import type { Paged } from '@/data/types'

export type MemberBetsQuery = {
  accountId: string
  /** Member being viewed — Lab accessLevel for that seat. */
  accountRole: Role
  source: MemberBetSource
  fromDate: string
  toDate: string
  time: MemberBetTime
  betType: MemberBetType
  page: number
  pageSize: number
  game?: string
  subGame?: string
  eventName?: string
  marketName?: string
  marketUniqueId?: string
  marketType?: string
  stakeFrom?: number
  stakeTo?: number
  /**
   * Lab `formData.name.username` from Enter Member Name ui-select.
   * Only set after a pick — free typing does not filter until selected.
   */
  memberUsername?: string | null
  memberId?: string | null
  /** When false, skip the request (unused for live-apply Bet List). */
  enabled?: boolean
  /** Staff Market access — omit / `all` skips the wire field. */
  bookmakerSessionFlag?: BookmakerSessionFlag
}

function levelLabel(role: Role | undefined): string {
  return role ? ROLE_LABEL[role] : 'Master'
}

function emptyPage(query: MemberBetsQuery): Paged<MemberSportBookBet> {
  return { rows: [], total: 0, page: query.page, pageSize: query.pageSize }
}

/** Wire body for `POST /v1/bets/getMemberBets` (whitebox-tested). */
export function buildMemberBetsRequestBody(
  query: MemberBetsQuery,
): GetMemberBetsRequest {
  const isPremium = query.source === 'premium-bm'
  const body: GetMemberBetsRequest = {
    userId: query.accountId,
    accessLevel: levelLabel(query.accountRole),
    fromDate: dayStartIso(query.fromDate),
    toDate: dayEndIso(query.toDate),
    time: query.time,
    betType: isPremium || query.time === 'Past' ? 'Matched' : query.betType,
    previousData: isPremium ? query.time === 'Past' : undefined,
    source: query.source,
    page: query.page,
    maxrow: query.pageSize,
  }
  const game = omitAllFilter(query.game)
  const subGame = omitAllFilter(query.subGame)
  const eventName = omitAllFilter(query.eventName)
  const marketName = omitAllFilter(query.marketName)
  const marketType = omitAllFilter(query.marketType)
  if (game) body.game = game
  if (subGame) body.subGame = subGame
  if (eventName) body.eventName = eventName
  if (marketName) body.marketName = marketName
  if (query.marketUniqueId?.trim()) body.marketUniqueId = query.marketUniqueId.trim()
  if (marketType) body.marketType = marketType
  if (query.stakeFrom != null && Number.isFinite(query.stakeFrom)) body.stakeFrom = query.stakeFrom
  if (query.stakeTo != null && Number.isFinite(query.stakeTo)) body.stakeTo = query.stakeTo
  const name = memberBetNameFilter(query.memberUsername, query.memberId)
  if (name) body.name = name
  if (query.bookmakerSessionFlag && query.bookmakerSessionFlag !== 'all') {
    body.bookmakerSessionFlag = query.bookmakerSessionFlag
  }
  return body
}

async function loadLive(query: MemberBetsQuery): Promise<Paged<MemberSportBookBet>> {
  const live = await betFetch<GetMemberBetsResponse>('/v1/bets/getMemberBets', {
    method: 'POST',
    body: JSON.stringify(buildMemberBetsRequestBody(query)),
  })
  return {
    rows: (live?.results ?? []).map(toMemberSportBookBet),
    total: live?.count ?? 0,
    page: query.page,
    pageSize: query.pageSize,
  }
}

/** Demo fixtures — approximate Lab filters against `listBets`. */
async function loadDemo(query: MemberBetsQuery): Promise<Paged<MemberSportBookBet>> {
  const isPremium = query.source === 'premium-bm'
  const statuses =
    query.time === 'Past'
      ? (['settled', 'void', 'cancelled'] as const)
      : !isPremium && query.betType === 'Unmatched'
        ? (['unmatched'] as const)
        : (['matched'] as const)
  const page = await api.listBets({
    accountId: query.accountId,
    range: { from: query.fromDate, to: query.toDate },
    statuses: [...statuses],
    search: query.memberUsername?.trim() || undefined,
    page: query.page,
    pageSize: query.pageSize,
  })
  let rows = page.rows.map(
    (b): MemberSportBookBet => ({
      id: b.id,
      memberId: b.memberId,
      userName: page.members.find((m) => m.id === b.memberId)?.username ?? b.memberId,
      placedAt: b.placedAt,
      matchedAt: b.matchedAt,
      eventName: b.eventName,
      eventType: b.sport,
      marketId: b.marketId,
      marketName: b.marketName,
      selection: b.selection,
      side: b.side,
      oddsPlaced: b.oddsPlaced,
      oddsMatched: b.oddsMatched ?? b.oddsPlaced,
      runs: null,
      stake: b.stake,
      matched: b.matched,
      unmatched: b.unmatched,
      status: b.status.toUpperCase(),
      betWinStatus: b.result === 'won' ? 'WON' : b.result === 'lost' ? 'LOST' : '',
      ip: '',
      currencyType: 'INR',
      betType: 'odds',
      isBookmaker: /bookmaker/i.test(b.marketName),
      isLineLadder: false,
    }),
  )
  if (isPremium) {
    // Demo world has no artemis product flag — keep rows so Premiumbook tab is reviewable.
    rows = rows.map((r, i) =>
      i % 2 === 0
        ? { ...r, marketName: `${r.marketName} (Bookmaker)`, isBookmaker: true, runs: null }
        : { ...r, marketName: `Fancy - ${r.marketName}`, runs: 40 + i, unmatched: 0 },
    )
  }
  if (query.stakeFrom != null) rows = rows.filter((r) => r.stake >= query.stakeFrom!)
  if (query.stakeTo != null) rows = rows.filter((r) => r.stake <= query.stakeTo!)
  const eventName = omitAllFilter(query.eventName)
  if (eventName) {
    rows = rows.filter((r) => r.eventName === eventName)
  }
  const marketName = omitAllFilter(query.marketName)
  if (marketName) {
    rows = rows.filter((r) => r.marketName === marketName)
  }
  const subGame = omitAllFilter(query.subGame)
  if (subGame) {
    rows = rows.filter((r) => r.eventType === subGame)
  }
  if (query.marketType === 'ArtemisBookmaker') {
    rows = rows.filter((r) => r.isBookmaker || /bookmaker/i.test(r.marketName))
  }
  if (query.marketType === 'ArtemisFancy') {
    rows = rows.filter((r) => /fancy/i.test(r.marketName))
  }
  return { rows, total: rows.length, page: query.page, pageSize: query.pageSize }
}

async function loadMemberBets(query: MemberBetsQuery): Promise<Paged<MemberSportBookBet>> {
  if (hasLiveApi()) {
    if (!hasLiveBet() || !query.accountId) return emptyPage(query)
    return loadLive(query)
  }
  return loadDemo(query)
}

export function useMemberBets(query: MemberBetsQuery) {
  const session = useSession()
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const withFlag: MemberBetsQuery = {
    ...query,
    bookmakerSessionFlag: query.bookmakerSessionFlag ?? bookmakerSessionFlag,
  }
  const ready = Boolean(query.accountId && (query.enabled ?? true))
  return useQuery({
    queryKey: keys.memberBets({ ...withFlag, viewer: session.data?.account.id }),
    queryFn: () => loadMemberBets(withFlag),
    enabled: ready,
    placeholderData: (previous) => previous,
  })
}
